import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack
} from "@mui/material";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import {
  CartSingleton,
  SessionExpiredError,
} from "../../SharedComponents/CartSingleton";
import Counter, { CounterDelegate } from "../../SharedComponents/Counter";
import { withNavigationHooks } from "../../SharedComponents/withNavigationHooks";
import { IInstrument, InstrumentDiscount } from "../../clients/answeringClient";
import { getUserRolesFromToken } from "../../utils/getUserRolesFromToken";
import DiscountTiersList from "../DiscountTiersList";
import { trackGtagEvent } from "../../utils/analytics";

interface State {
  purchaseQuantity: number;
  finished: boolean;
  startingQuantity: number;
  isSaving: boolean;
  errorMessage?: string;
}

interface Props {
  instrument: IInstrument;
  delegate: CartModalDelegate;
  navigationHooks: NavigateFunction;
}

const currencyFormatter = new Intl.NumberFormat("pt-br", {
  style: "currency",
  currency: "BRL",
});

const formatCurrency = (valueInCents: number) =>
  currencyFormatter.format((valueInCents ?? 0) / 100);

export interface CartModalDelegate {
  shouldCloseModal: () => void;
  didRequestInstrumentDiscounts(instrument: IInstrument): void;
  applicationShouldLogout(): void;
}

export class CartModal
  extends React.Component<Props, State>
  implements CounterDelegate {
  private cartListener = () => this.syncQuantityFromCart();
  private isUnmounted = false;

  constructor(props: Props) {
    super(props);

    const existingItem = CartSingleton.shared.getCartInstrument(
      props.instrument.id
    );
    const startingQuantity = existingItem?.quantity ?? 0;

    this.state = {
      purchaseQuantity: startingQuantity,
      finished: false,
      startingQuantity,
      isSaving: false,
    };
  }

  componentDidMount(): void {
    this.isUnmounted = false;
    CartSingleton.shared.addCartUpdatedListener(this.cartListener);
    void CartSingleton.shared
      .ensureLoaded()
      .then(() => {
        this.syncQuantityFromCart();
      })
      .catch((error: unknown) => {
        // No retry can revive a dead session, so the transient fallback would
        // leave the buyer editing a quantity nothing can write.
        if (error instanceof SessionExpiredError) {
          this.props.delegate.applicationShouldLogout();
          return;
        }
        this.syncQuantityFromCart();
      });
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    CartSingleton.shared.removeCartUpdatedListener(this.cartListener);
  }

  // delegate
  didSetValueTo(value: number): void {
    this.setState({
      purchaseQuantity: value,
    });
  }

  private handleSelectTierQuantity = (minQuantity: number) => {
    this.setState({
      purchaseQuantity: Math.max(0, minQuantity),
    });
  };

  // internals
  didClickClose() {
    // Esc and the backdrop reach here too, and a dismissed-and-reopened modal
    // carries a fresh in-flight guard over the same pending write.
    if (this.state.isSaving) {
      return;
    }

    this.setState({ finished: false, errorMessage: undefined }, () => {
      this.props.delegate.shouldCloseModal();
    });
  }

  async didClickAddToCart(): Promise<void> {
    if (this.state.isSaving) {
      return;
    }

    const { purchaseQuantity, startingQuantity } = this.state;

    this.setState({ isSaving: true, errorMessage: undefined });

    try {
      await CartSingleton.shared.ensureLoaded();
      const current = CartSingleton.shared.getCartInstrument(
        this.props.instrument.id
      );
      const currentQuantity = current?.quantity ?? 0;

      // The displayed quantity is seeded at construction, so against an
      // invalidated cache it reads 0 — a number the user never saw, and
      // writing over the real one is never their intent.
      if (currentQuantity !== startingQuantity) {
        if (!this.isUnmounted) {
          this.setState({
            purchaseQuantity: currentQuantity,
            startingQuantity: currentQuantity,
            errorMessage: "O carrinho foi atualizado, confira a quantidade.",
          });
        }
        return;
      }

      await CartSingleton.shared.setCartInstrument(
        this.props.instrument.id,
        purchaseQuantity
      );
      if (this.isUnmounted) {
        return;
      }
      this.trackAddToCart(purchaseQuantity);
      this.setState({ finished: true });
    } catch (error: any) {
      console.warn("Cart update failed", error);
      if (this.isUnmounted) {
        return;
      }
      if (error instanceof SessionExpiredError) {
        this.props.delegate.applicationShouldLogout();
        return;
      }
      // Status taxonomy: see ManagedAssignmentRow.didClickAddToCart.
      const status = error?.response?.status;
      this.setState({
        errorMessage:
          (status === 400 || status === 403) && error?.response?.data?.message
            ? error.response.data.message
            : "Não foi possível atualizar o carrinho. Tente novamente em instantes.",
      });
    } finally {
      if (!this.isUnmounted) {
        this.setState({ isSaving: false });
      }
    }
  }

  didClickGoToCheckout() {
    this.props.navigationHooks("/checkout/review");
  }

  private syncQuantityFromCart() {
    if (this.isUnmounted) {
      return;
    }

    const item = CartSingleton.shared.getCartInstrument(
      this.props.instrument.id
    );
    const quantity = item?.quantity ?? 0;

    this.setState({
      startingQuantity: quantity,
      purchaseQuantity: quantity,
    });
  }

  private trackAddToCart(quantity: number) {
    if (
      quantity <= 0 ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    const priceInBrlCents = this.props.instrument.priceInBrlCents ?? 0;
    const unitPrice = priceInBrlCents / 100;
    const totalValue = unitPrice * quantity;
    const roles = getUserRolesFromToken();

    const fbq = window.fbq;
    if (fbq) {
      fbq("track", "AddToCart", {
        content_ids: [String(this.props.instrument.id)],
        content_type: "product",
        contents: [
          {
            id: String(this.props.instrument.id),
            quantity,
            item_price: unitPrice,
          },
        ],
        currency: "BRL",
        value: totalValue,
        roles,
      });
    }

    trackGtagEvent("add_to_cart", {
      currency: "BRL",
      value: totalValue,
      items: [
        {
          item_id: String(this.props.instrument.id),
          quantity,
          price: unitPrice,
        },
      ],
      roles,
    });
  }

  private getApplicableDiscountRule(quantity: number): InstrumentDiscount | null {
    if (!quantity || quantity <= 0) {
      return null;
    }

    const rules = this.props.instrument.discountRules ?? [];

    return rules.reduce<InstrumentDiscount | null>((selected, rule) => {
      const isInRange =
        quantity >= rule.minQty &&
        (rule.maxQty == null || quantity <= rule.maxQty);

      if (!isInRange) {
        return selected;
      }

      if (!selected || rule.minQty > selected.minQty) {
        return rule;
      }

      return selected;
    }, null);
  }

  private getDiscountedUnitPriceCents(quantity: number): number | null {
    const matchingRule = this.getApplicableDiscountRule(quantity);
    if (!matchingRule) {
      return null;
    }

    if (matchingRule.discountedUnitPriceCents != null) {
      return matchingRule.discountedUnitPriceCents;
    }

    if (matchingRule.unitPriceOverrideCents != null) {
      return matchingRule.unitPriceOverrideCents;
    }

    if (matchingRule.discountPct > 0) {
      const baseUnitPriceCents = this.props.instrument.priceInBrlCents ?? 0;
      const discountFactor = 1 - matchingRule.discountPct / 100;
      return Math.round(baseUnitPriceCents * discountFactor);
    }

    return null;
  }

  renderPurchaseContent() {
    const { purchaseQuantity } = this.state;
    const baseUnitPriceCents = this.props.instrument.priceInBrlCents ?? 0;
    const discountedUnitPriceCents =
      this.getDiscountedUnitPriceCents(purchaseQuantity);
    const effectiveUnitPriceCents =
      discountedUnitPriceCents ?? baseUnitPriceCents;

    const totalCents = effectiveUnitPriceCents * purchaseQuantity;
    const baseTotalCents = baseUnitPriceCents * purchaseQuantity;
    const hasProgressiveDiscount =
      purchaseQuantity > 0 &&
      discountedUnitPriceCents !== null &&
      discountedUnitPriceCents < baseUnitPriceCents;

    const formattedPrice = formatCurrency(totalCents);
    const formattedBasePrice = formatCurrency(baseTotalCents);
    const hasDiscountRules = (this.props.instrument.discountRules?.length ?? 0) > 0;
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
      >
        {this.state.errorMessage && (
          <Typography variant="body2" color="error" textAlign="center" my={1}>
            {this.state.errorMessage}
          </Typography>
        )}

        <Typography variant="h6" alignSelf="flex-start" fontWeight={600}>
          Instrumento: {this.props.instrument.title}
        </Typography>
        <Typography variant="subtitle1" mb={1} alignSelf="flex-start">
          Descrição: {this.props.instrument.description}
        </Typography>

        {hasDiscountRules && (
          <Box
            sx={{
              alignSelf: "stretch",
              width: "100%",
              mt: 1
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={1} color={'#F19421'}>
              Descontos
            </Typography>
            <Box
              sx={{
                maxHeight: { xs: 320, sm: 360 },
                overflowY: "auto",
              }}
            >
              <DiscountTiersList
                discounts={this.props.instrument.discountRules ?? []}
                baseUnitPriceCents={this.props.instrument.priceInBrlCents}
                appliedQuantity={this.state.purchaseQuantity}
                onSelectTier={this.handleSelectTierQuantity}
              />
            </Box>
          </Box>
        )}
        <Stack gap={2} direction={'row'} alignItems={'center'} mt={3} justifyContent={'space-between'} width={'100%'}>
          <Box>
            <Counter
              minValue={0}
              delegate={this}
              startValue={this.state.purchaseQuantity}
              disabled={this.state.isSaving}
            />
          </Box>

          <Box textAlign="center">
            <Typography variant="h5">
              Total dos itens: {formattedPrice}
            </Typography>
            {hasProgressiveDiscount && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: "line-through" }}
              >
                Total sem descontos: {formattedBasePrice}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    );
  }

  renderPurchaseActions() {
    return (
      <Box display={'flex'} gap={1} flexWrap={'wrap'}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            this.didClickClose();
          }}
          disabled={this.state.isSaving}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            void this.didClickAddToCart();
          }}
          disabled={this.state.isSaving}
        >
          {this.state.startingQuantity > 0
            ? "Atualizar Carrinho"
            : "Adicionar ao Carrinho"}
        </Button>
      </Box>
    );
  }

  renderGoToCheckoutContent() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" alignSelf="flex-start" fontWeight={600}>
          Instrumento: {this.props.instrument.title}
        </Typography>
        <Typography variant="subtitle1" alignSelf="flex-start">
          Descrição: {this.props.instrument.description}
        </Typography>
        <Typography variant="subtitle1" alignSelf="flex-start">
          Quantidade: {this.state.purchaseQuantity}
        </Typography>
      </Box>
    );
  }

  renderGoToCheckoutActions() {
    return (
      <>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            this.didClickClose();
          }}
        >
          Adicionar mais itens
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            this.didClickGoToCheckout();
          }}
        >
          Revisar e Pagar
        </Button>
      </>
    );
  }

  render() {
    const isFinished = this.state.finished;
    const TitleIcon = isFinished
      ? ShoppingCartCheckoutOutlinedIcon
      : AddShoppingCartOutlinedIcon;
    const title = isFinished ? "Carrinho Atualizado" : "Adicionar ao Carrinho";

    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle variant="modal">
          <TitleIcon />
          {title}
        </DialogTitle>
        <DialogContent variant="modal">
          {!isFinished && this.renderPurchaseContent()}
          {isFinished && this.renderGoToCheckoutContent()}
        </DialogContent>
        <DialogActions variant="modal" sx={{ flexWrap: "wrap" }}>
          {!isFinished && this.renderPurchaseActions()}
          {isFinished && this.renderGoToCheckoutActions()}
        </DialogActions>
      </Dialog>
    );
  }
}

export default withNavigationHooks(CartModal);
